import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const NavigationContext = createContext(null);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};

export const NavigationProvider = ({ children }) => {
  const [navigationHistory, setNavigationHistory] = useState(['/dashboard']);
  const [currentTab, setCurrentTab] = useState('home');
  const [contentKey, setContentKey] = useState(Date.now()); // Force re-render key
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Update current tab based on location
    const pathToTab = {
      '/dashboard': 'home',
      '/': 'home',
      '/studios': 'studios',
      '/queue': 'queue',
      '/analytics': 'analytics',
      '/profile': 'profile'
    };

    const newTab = pathToTab[location.pathname] || 'home';
    
    // Add to history if it's different from current
    if (newTab !== currentTab) {
      setNavigationHistory(prev => {
        const newHistory = [...prev, location.pathname];
        // Keep only last 5 items
        return newHistory.slice(-5);
      });
      setCurrentTab(newTab);
      // Force content re-render when tab changes
      setContentKey(Date.now());
    }

    // Handle Android back button
    const handleBackButton = (event) => {
      event.preventDefault();
      goBack();
    };

    // Add back button listener for mobile
    window.addEventListener('popstate', handleBackButton);
    
    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [location.pathname, currentTab]);

  const goBack = () => {
    if (navigationHistory.length > 1) {
      const previousPath = navigationHistory[navigationHistory.length - 2];
      setNavigationHistory(prev => prev.slice(0, -1));
      navigate(previousPath, { replace: true });
      return true;
    }
    // Default to dashboard if no history
    navigate('/dashboard', { replace: true });
    return false;
  };

  const navigateTo = (path) => {
    // Prevent duplicate entries
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const getTabRoute = (tab) => {
    const tabToPath = {
      'home': '/dashboard',
      'studios': '/studios',
      'queue': '/queue',
      'analytics': '/analytics',
      'profile': '/profile'
    };
    return tabToPath[tab] || '/dashboard';
  };

  const value = {
    navigationHistory,
    currentTab,
    contentKey,
    goBack,
    navigateTo,
    getTabRoute,
    setCurrentTab
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};